import { useNavigate, useParams } from "react-router-dom";
import { Back, Open } from "../../../compoment/icon";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  aiSelfieArtImageUrl,
  getActiveAiSelfieArtsByCategory,
  type AiSelfieArt,
} from "../../../services/aiSelfieArtService";
import {
  aiSelfieCategoryBackgroundUrl,
  aiSelfieCategoryImageUrl,
  getActiveAiSelfieCategories,
  type AiSelfieCategory,
} from "../../../services/aiSelfieCategoryService";
import Loader from "../../../compoment/ui/Loader";
import {
  enrichStationWithThemeBackgrounds,
  persistSelectedAiSelfieArt,
  persistStationSelection,
  resetKioskLandingAppState,
} from "../../../utils/stationFlowStorage";

type StationRecord = {
  id?: string | number;
  name?: string;
  type?: string;
  imgURL?: string;
  txtImg?: string;
  backgroundImageUrl?: string;
};

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function readStationData(): StationRecord | null {
  try {
    const raw = localStorage.getItem("stationData");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storedStationMatchingSlug(slug: string): StationRecord | null {
  const stored = readStationData();
  if (!stored?.type?.trim() || stored.type.trim() !== slug) return null;
  if (stored.id === undefined || stored.id === null || String(stored.id).trim() === "") return null;
  return stored;
}

const Idle = () => {
  const navigate = useNavigate();
  const { slug: slugParam } = useParams();
  const slug = decodeURIComponent(slugParam ?? "").trim();
  const dispatch = useDispatch();
  const [arts, setArts] = useState<AiSelfieArt[]>([]);
  const [artsLoading, setArtsLoading] = useState(true);
  const [stationData, setStationData] = useState<StationRecord | null>(() =>
    slug ? storedStationMatchingSlug(slug) : null
  );
  const [stationResolving, setStationResolving] = useState(() => {
    if (!slug) return false;
    return !storedStationMatchingSlug(slug);
  });
  const [stationResolveError, setStationResolveError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setStationData(null);
      setStationResolving(false);
      setStationResolveError("Missing category in URL");
      return;
    }

    const matched = storedStationMatchingSlug(slug);
    if (matched) {
      setStationData(matched);
      setStationResolving(false);
      setStationResolveError(null);
      return;
    }

    let cancelled = false;
    setStationResolving(true);
    setStationResolveError(null);

    (async () => {
      const res = await getActiveAiSelfieCategories();
      if (cancelled) return;

      if (!res.success || res.data.length === 0) {
        setStationData(null);
        setStationResolveError(res.error || "Could not load categories");
        setStationResolving(false);
        return;
      }

      const match = res.data.find((cat: AiSelfieCategory) => {
        const catSlug = (cat.slug || slugify(cat.name || "")).trim();
        return catSlug === slug;
      });

      if (!match) {
        setStationData(null);
        setStationResolveError("This category was not found or is inactive.");
        setStationResolving(false);
        return;
      }

      const item: StationRecord = {
        id: String(match.id),
        name: match.name,
        imgURL: aiSelfieCategoryImageUrl(match),
        backgroundImageUrl: aiSelfieCategoryBackgroundUrl(match),
        type: (match.slug || slugify(match.name || "")).trim(),
      };

      persistStationSelection(item);

      const selectedRaw = localStorage.getItem("selectedStation");
      if (selectedRaw) {
        try {
          const sel = JSON.parse(selectedRaw);
          if (sel?.type?.trim() && sel.type.trim() !== slug) {
            localStorage.removeItem("selectedStation");
          }
        } catch {
          localStorage.removeItem("selectedStation");
        }
      }

      setStationData(item);
      setStationResolving(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    resetKioskLandingAppState(dispatch);
  }, [dispatch]);

  useEffect(() => {
    const categoryId = stationData?.id;
    if (categoryId === undefined || categoryId === null || String(categoryId).trim() === "") {
      setArts([]);
      setArtsLoading(false);
      return;
    }

    let cancelled = false;
    setArtsLoading(true);

    (async () => {
      const res = await getActiveAiSelfieArtsByCategory(String(categoryId));
      if (cancelled) return;
      setArts(res.success ? res.data : []);
      setArtsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [stationData?.id]);

  const stationMatchesSlug =
    Boolean(slug) &&
    stationData != null &&
    typeof stationData.type === "string" &&
    stationData.type.trim() === slug;

  const stationOutOfSync = Boolean(slug) && stationData != null && !stationMatchesSlug;

  const handleArtSelect = (art: AiSelfieArt) => {
    persistSelectedAiSelfieArt(art);
    const themedStation = enrichStationWithThemeBackgrounds(stationData, art);
    persistStationSelection(themedStation);
    navigate(`/${slugify(art.name)}/idle`, {
      state: {
        selectedArt: art,
        stationData: themedStation,
      },
    });
  };

  if (stationResolving || stationOutOfSync) {
    return (
      <Loader />
    );
  }

  const hasStationId =
    stationData != null &&
    stationData.id !== undefined &&
    stationData.id !== null &&
    String(stationData.id).trim() !== "";

  if (!hasStationId || stationResolveError) {
    return (
      <div className="d-flex flex-column gap-3 justify-content-center align-items-center vh-100 px-4 text-center">
        <p className="mb-0">{stationResolveError || "This station could not be loaded."}</p>
        <button type="button" className="btn btn-primary" onClick={() => navigate("/")}>
          Back to home
        </button>
      </div>
    );
  }

  const showArtsGrid = !artsLoading && arts.length > 0;
  const fallbackImage = stationData.txtImg || stationData.imgURL;
  const pageBackground = stationData.backgroundImageUrl || fallbackImage;

  return (
    <section
      className={`homeSecreen idleSectionNew idleCategoryScreen idleArtSelectionScreen${showArtsGrid ? " idleCategoryArts" : ""}`}
    >
      {showArtsGrid ? (
        <>
        <button type="button" className="idleBackButton" onClick={() => navigate('/')}>
          <Back />
          Back
        </button>
        <div className="idleCategoryBody">
          <header className="idleCategoryHeader">
            <h1 className="idleCategoryTitle">
              {stationData.name} Selfie Station
            </h1>
          </header>

          <div className="idleArtsGrid">
            {arts.map((art) => {
              const artImage = aiSelfieArtImageUrl(art);

              return (
                <article className="idleArtCard" key={art.id}>
                  <div
                    className="idleArtMedia"
                  >
                    <img src={artImage} alt={art.name} draggable={false} />
                  </div>
                  <footer className="idleArtFooter">
                    <div className="idleArtName">{art.name}</div>
                    <button type="button" onClick={() => handleArtSelect(art)}>
                      Start
                      <span>
                        <Open />
                      </span>
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        </div>
        </>
      ) : artsLoading && (
        <Loader />
      )}

    </section>
  );
};

export default Idle;
